import { Button, TableItem } from 'components/atoms';
import PopupDeleteWarning from 'components/molecules/popup/delete-warning/PopupDeleteWarning';
import { Table } from 'components/organisms';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { useParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { groupContractsByTrack } from 'middleware/music-product-digital/settlementInfoQueryToForm';
import { useTranslation } from 'react-i18next';
import { PiCaretLeftBold, PiNavigationArrow, PiTrash } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetMusicProductDigitalContractFuture } from 'services/music-product-digital/settlementInfo';
import { useClearContractFuture } from 'services/product-contract';
import { PRODUCT_TYPE } from 'services/contract/types';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate } from 'utils/global/date.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const MusicProductDigitalContractFuture = () => {
  usePermissionGuard('ContractPermissionsLIST**');

  const id = useParamId();

  const { status, values }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  const navigateToNewWindow = useNewWindowNavigate();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const future = useGetMusicProductDigitalContractFuture(id);

  const contractsByTrack = groupContractsByTrack(
    future.data?.settlementInfo?.perTrackContracts ?? []
  );

  const clearContractFuture = useClearContractFuture();

  const onClearContractFuture = async (actions: PopupActions) =>
    await clearContractFuture.mutateAsync(
      {
        productId: id,
        productType: PRODUCT_TYPE.Digital,
      },
      {
        onSuccess: () => {
          future.refetch();
          actions.close();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() =>
                navigate(
                  status?.displayMode
                    ? `/music-product-digital/${id}/contracts`
                    : `/music-product-digital/edit/${id}/contracts`
                )
              }>
                            <Button.Description description={t('button.back')} />

                            <Button.Icon icon={<PiCaretLeftBold />} />
            </Button>
          }
          title={
            future.data?.settlementInfo
              ? `${t('input.label.per-track-settlement')}: ${
                  future.data.settlementInfo.perTrackSettlement
                    ? t('input.option.yes')
                    : t('input.option.no')
                }`
              : undefined
          }
          topbarContent={
            <PopupDeleteWarning
              onRemove={onClearContractFuture}
              disabledTrigger={!future.data?.settlementInfo?.contracts.length}
              name={`${t('contract-template.contract.what-future')} ${
                values.mainTitle.title
              }`}
              trigger={
                <Button
                  isClear
                  variant={BUTTON_VARIANT.remove}
                  onlyIcon
                  disabled={!future.data?.settlementInfo?.contracts.length}>
                                    <Button.Description description={t('button.remove')} />

                                    <Button.Icon icon={<PiTrash />} />
                </Button>
              }
            />
          }
        />
      }>
      <div className='flex flex-col-reverse gap-8 w-full'>
        <div className='flex flex-col-reverse mb-20 w-full gap-2'>
          {contractsByTrack.reverse().map((track, trackIndex) => (
            <Table
              id={'MusicProductDigitalContractFuture'}
              key={trackIndex}
              size='compact'
              data={{
                nodes: track.contracts.map(c => ({ ...c, id: c.contractId })),
              }}
              columns={[
                {
                  label: `${track.title}`,
                  renderCell: ({ labelName: name }) => (
                    <TableItem>
                      <TableItem.Text text={name} />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.contract`),
                  renderCell: contract => (
                    <TableItem>
                      <TableItem.Text text={contract.contractName} />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.date`),
                  renderCell: contract => (
                    <TableItem>
                      <TableItem.Text
                        text={`${formatDate(contract.startDate)}`}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.division`),
                  renderCell: ({ divisionPercentage, id: index }) => (
                    <TableItem>
                      <TableItem.Text text={`${divisionPercentage ?? '-'}%`} />
                    </TableItem>
                  ),
                },

                {
                  label: '',
                  renderCell: ({ id: index, labelId, contractId }) => (
                    <TableItem justifyPosition='end'>
                      <Button
                        isClear
                        onlyIcon
                        onClick={() =>
                          navigateToNewWindow(
                            `label/${labelId}/contract/${contractId}/form`
                          )
                        }>
                                                <Button.Description description={t('button.go-to-form')} />

                                                <Button.Icon
                          icon={<PiNavigationArrow transform='scale(-1,1)' />}
                        />
                      </Button>
                    </TableItem>
                  ),
                },
              ]}
            />
          ))}
        </div>
        <div className='flex w-full'>
          <Table
            id={'MusicProductDigitalContractFuture'}
            size='compact'
            loading={future.isLoading}
            data={{
              nodes:
                future.data?.settlementInfo?.contracts.map(c => ({
                  ...c,
                  id: c.contractId,
                })) ?? [],
            }}
            columns={[
              {
                label: `${t('input.label.global')}`,
                renderCell: ({ labelName: name }) => (
                  <TableItem>
                    <TableItem.Text text={name} />
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t(`input.label.contract`),
                renderCell: contract => (
                  <TableItem>
                    <TableItem.Text text={contract.contractName} />
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t(`input.label.date`),
                renderCell: contract => (
                  <TableItem>
                    <TableItem.Text
                      text={`${formatDate(contract.startDate)}`}
                    />
                  </TableItem>
                ),
                resize: true,
              },

              {
                label: t(`input.label.division`),
                renderCell: ({ divisionPercentage, id: index }) => (
                  <TableItem>
                    <TableItem.Text text={`${divisionPercentage ?? '-'}%`} />
                  </TableItem>
                ),
              },
              {
                label: '',
                renderCell: ({ id: index, labelId, contractId }) => (
                  <TableItem justifyPosition='end'>
                    <Button
                      isClear
                      onlyIcon
                      onClick={() =>
                        navigateToNewWindow(
                          `label/${labelId}/contract/${contractId}/form`
                        )
                      }>
                                            <Button.Description description={t('button.go-to-form')} />

                                            <Button.Icon
                        icon={<PiNavigationArrow transform='scale(-1,1)' />}
                      />
                    </Button>
                  </TableItem>
                ),
              },
            ]}
          />
        </div>
      </div>
    </TopbarPage>
  );
};

export default MusicProductDigitalContractFuture;
